import { CoreInputViewNew, CoreLayoutView, CoreTextView, ORIENTATION_ENUM, type TCoreLayoutOptions, useThemeHook } from '@core/widgets';
import { TCommonData, TMargin, TWidget } from '@libs/types';
import React, { ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent, useCallback, useLayoutEffect, useRef } from 'react';
import { getSemanticColors, SemanticColor } from '../../theme/colors';
import { TextStyle } from '../../types/styles';
import styles from './otpInput.module.css';

type TUnderlineStyleProps = {
    height?: number;
    activeColor?: string;
    errorColor?: string;
};

type TOtpInputPalette = {
    otpInputBoxActive?: TCoreLayoutOptions;
    otpInputUnderline?: TUnderlineStyleProps;
    otpInputBox?: TCoreLayoutOptions;
} & TCoreLayoutOptions;

export type TOtpInputViewProps = {
    widget: TWidget<TCommonData, TOtpInputPalette>;
    uid?: string;
    value?: string;
    isFocused?: boolean;
    codeLength?: number;
    dividerPosition?: number;
    isHideSymbols?: boolean;
    allowAllSymbols?: boolean;
    autoFocus?: boolean;
    autoComplete?: string;
    inputType?: string;
    tabIndex?: number;
    isError?: boolean;
    isDisabled?: boolean;
    isHide?: boolean;
    errorMessage?: string;
    margin?: TMargin;
    parentStyleNames?: string[];
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
    onClearOnError?: () => void;
};

const OtpInputView = ({
    widget,
    uid,
    value = '',
    isFocused = false,
    codeLength = 6,
    dividerPosition = 0,
    isHideSymbols = false,
    allowAllSymbols = false,
    autoFocus = false,
    autoComplete,
    inputType,
    tabIndex,
    isError = false,
    isDisabled = false,
    isHide = false,
    errorMessage,
    margin,
    parentStyleNames = [],
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onPaste,
    onClearOnError,
}: TOtpInputViewProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const chars = value.split('');
    const filledCount = Math.min(chars.length, codeLength);
    const activeIndex = Math.min(filledCount, codeLength - 1);

    const resolvedWidget = { uid: uid ?? '', data: {}, options: {} } as unknown as TWidget<TCommonData, TOtpInputPalette>;
    const theme = useThemeHook<TOtpInputPalette>(widget);
    const semanticColors = getSemanticColors(widget.version);

    const boxStyle = theme.palette?.otpInputBox ?? {};
    const accentStyle = theme.palette?.otpInputUnderline ?? {};

    const boxStyleProps = {
        width: boxStyle.width ?? 40,
        height: boxStyle.height ?? 56,
        borderRadius: boxStyle.borderRadius ?? 6,
        borderStroke: boxStyle.borderStroke ?? 1,
        borderColor: boxStyle.borderColor ?? semanticColors[SemanticColor.BorderPrimary],
        gap: boxStyle.gap ?? 8,
        lineY: 'center' as const,
    };

    const accentStyleProps = {
        backgroundColor: accentStyle.activeColor ?? semanticColors[SemanticColor.IconEmphasised],
        error: { backgroundColor: accentStyle.errorColor ?? semanticColors[SemanticColor.IconError] },
        height: accentStyle.height ?? 2,
    };

    const resolvedInputType = inputType ?? (isHideSymbols ? 'password' : 'text');
    const resolvedAutoComplete = autoComplete ?? (isHideSymbols ? 'cc-csc' : 'one-time-code');

    const isAddSeparator = (index: number): boolean => dividerPosition > 0 && index + 1 !== codeLength && (index + 1) % dividerPosition === 0;

    useLayoutEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus();
        }
    }, [autoFocus]);

    const alignCursor = useCallback(() => {
        const input = inputRef.current;
        if (!input) return;
        const len = input.value.length;
        input.setSelectionRange(len, len);
    }, []);

    const handleLocalFocus = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            onFocus?.(e);
            requestAnimationFrame(() => {
                alignCursor();
            });
        },
        [onFocus, alignCursor],
    );

    const handleLocalClick = useCallback(() => {
        if (isError) {
            onClearOnError?.();
            inputRef.current?.setSelectionRange(0, 0);
            return;
        }
        alignCursor();
    }, [isError, onClearOnError, alignCursor]);

    return (
        <CoreLayoutView
            uid={uid}
            widget={resolvedWidget}
            styleName="otpInputLayout"
            parentStyleNames={parentStyleNames}
            isHide={isHide}
            isDisabled={isDisabled}
            styleProps={{
                orientation: ORIENTATION_ENUM.VERTICAL,
                lineX: 'center',
                gap: 16,
                margin,
                opacity: isDisabled ? 0.4 : undefined,
            }}
        >
            <CoreLayoutView
                uid={uid ? `${uid}_row` : undefined}
                className={styles.row}
                styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, lineX: 'center', lineY: 'center', gap: 8 }}
            >
                <CoreInputViewNew
                    inputRef={inputRef}
                    uid={uid ? `${uid}_input` : undefined}
                    dataTest={uid ? `${uid}_input` : undefined}
                    isCapture
                    className={styles.captureInput}
                    value={value}
                    isDisabled={isDisabled}
                    isError={isError}
                    type={resolvedInputType}
                    inputMode={allowAllSymbols ? 'text' : 'numeric'}
                    autoComplete={resolvedAutoComplete}
                    maxLength={codeLength}
                    tabIndex={tabIndex}
                    onChange={onChange}
                    onFocus={handleLocalFocus}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    onClick={handleLocalClick}
                />
                {Array.from({ length: codeLength }, (_, index) => {
                    const char = chars[index] ?? '';
                    const showAccent = isError || (isFocused && index === activeIndex);
                    const showCaret = !isError && isFocused && index === activeIndex && char === '';

                    const currentBoxStyleName = isError ? 'otpInputBoxError' : isFocused && index === activeIndex ? 'otpInputBoxActive' : 'otpInputBox';

                    return (
                        <React.Fragment key={index}>
                            <CoreLayoutView
                                uid={uid ? `${uid}_box_${index}` : undefined}
                                styleName={currentBoxStyleName}
                                parentStyleNames={parentStyleNames}
                                className={styles.box}
                                styleProps={boxStyleProps}
                            >
                                {char !== '' &&
                                    (isHideSymbols ? (
                                        <CoreLayoutView
                                            uid={uid ? `${uid}_bullet_${index}` : undefined}
                                            styleName="otpInputBullet"
                                            parentStyleNames={parentStyleNames}
                                            styleProps={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: semanticColors[SemanticColor.IconEmphasised],
                                                flexShrink: 0,
                                            }}
                                        />
                                    ) : (
                                        <CoreTextView
                                            uid={uid ? `${uid}_digit_${index}` : undefined}
                                            styleName="otpInputDigit"
                                            parentStyleNames={parentStyleNames}
                                            as="span"
                                            text={char}
                                            styleProps={{ ...TextStyle.header, main: semanticColors[SemanticColor.TextPrimary] }}
                                        />
                                    ))}
                                {showCaret && (
                                    <CoreLayoutView
                                        uid={uid ? `${uid}_caret_${index}` : undefined}
                                        styleName="otpInputCaret"
                                        parentStyleNames={parentStyleNames}
                                        className={styles.caret}
                                        styleProps={{
                                            width: 2,
                                            height: 28,
                                            borderRadius: 1,
                                            backgroundColor: semanticColors[SemanticColor.IconEmphasised],
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                {showAccent && (
                                    <CoreLayoutView
                                        uid={uid ? `${uid}_underline_${index}` : undefined}
                                        className={styles.accent}
                                        isError={isError}
                                        styleProps={accentStyleProps}
                                    />
                                )}
                            </CoreLayoutView>
                            {isAddSeparator(index) && (
                                <CoreTextView
                                    uid={uid ? `${uid}_divider_${index}` : undefined}
                                    styleName="otpInputDivider"
                                    parentStyleNames={parentStyleNames}
                                    as="span"
                                    text="-"
                                    styleProps={{ ...TextStyle.header, main: semanticColors[SemanticColor.TextSecondary] }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </CoreLayoutView>
            {isError && errorMessage && (
                <CoreTextView
                    uid={uid ? `${uid}_error` : undefined}
                    styleName="otpInputError"
                    parentStyleNames={parentStyleNames}
                    text={errorMessage}
                    styleProps={{ ...TextStyle.main, main: semanticColors[SemanticColor.TextError], textAlign: 'center' }}
                />
            )}
        </CoreLayoutView>
    );
};

export { OtpInputView };
